// Passwords people choose themselves (routes/auth_password.py) and the
// operator's name (routes/operator_name.py). None of these is an iPhone app
// operation.
//
// - a set-password link: check it (nothing spent), then set the password,
//   which signs this browser in;
// - Settings → Password: change your own (the current one is checked under
//   the sign-in limiter; every other session ends, this browser stays);
// - Settings → General → Your name: how agents address you.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";
import type { components } from "@/types/api.gen";

type S = components["schemas"];

export type PasswordLinkCheck = S["PasswordLinkCheck"];
export type SessionInfo = S["SessionInfo"];
export type OperatorName = S["OperatorName"];

export function checkPasswordLink(token: string): Promise<PasswordLinkCheck> {
  return apiFetch<PasswordLinkCheck>("/api/auth/password-link/check", { method: "POST", body: { token } });
}

export function setPasswordFromLink(token: string, password: string): Promise<SessionInfo> {
  return apiFetch<SessionInfo>("/api/auth/password-link", { method: "POST", body: { token, password } });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: (body: { current: string; new: string }) =>
      apiFetch<null>("/api/auth/password", { method: "POST", body }),
    meta: { errorHandled: true },
  });
}

const operatorKey = (customer: string) => ["operator-name", customer] as const;

export function useOperatorName(customer: string | null) {
  return useQuery({
    queryKey: operatorKey(customer ?? ""),
    queryFn: () => apiFetch<OperatorName>(`/api/customers/${encodeURIComponent(customer ?? "")}/operator`),
    enabled: Boolean(customer),
  });
}

export function useSaveOperatorName(customer: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) =>
      apiFetch<OperatorName>(`/api/customers/${encodeURIComponent(customer)}/operator`, { method: "PUT", body: { name } }),
    onSuccess: (saved) => qc.setQueryData(operatorKey(customer), saved),
  });
}
